import React, { useEffect, useRef, useState } from "react";
import { createPortal } from "react-dom";

/**
 * Modal chỉnh sửa dự án (tên + prompt). Dùng cho owner/editor.
 *
 * Props:
 *   open      : boolean
 *   loading   : boolean
 *   project   : { _id, name, prompt }
 *   onSave    : ({ name, prompt }) => void | Promise<void>
 *   onClose   : () => void
 */
function EditProjectModal({ open, loading = false, project, onSave, onClose }) {
  const [name, setName] = useState("");
  const [prompt, setPrompt] = useState("");
  const inputRef = useRef(null);

  useEffect(() => {
    if (open && project) {
      setName(project.name || "");
      setPrompt(project.prompt || "");
      setTimeout(() => inputRef.current?.focus(), 50);
    }
  }, [open, project]);

  useEffect(() => {
    if (!open) return;
    const onKey = (e) => {
      if (e.key === "Escape" && !loading) onClose?.();
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [open, loading, onClose]);

  if (!open) return null;

  const trimmed = name.trim();

  const submit = () => {
    if (!trimmed || loading) return;
    onSave?.({ name: trimmed, prompt: prompt.trim() });
  };

  return createPortal(
    <div
      className="fixed inset-0 z-[300] flex items-center justify-center bg-black/40 p-4"
      onClick={() => !loading && onClose?.()}
      style={{ animation: "fadeIn 0.12s ease-out" }}
    >
      <div
        className="bg-white rounded-2xl shadow-2xl max-w-md w-full overflow-hidden"
        onClick={(e) => e.stopPropagation()}
        style={{ animation: "pimSlideUp 0.15s ease-out" }}
      >
        <div className="p-5">
          <h3 className="text-base font-semibold text-gray-900">
            Chỉnh sửa dự án
          </h3>
          <p className="mt-1 text-sm text-gray-500">
            Cập nhật tên và prompt định hướng AI cho dự án.
          </p>
        </div>

        <div className="px-5 pb-1">
          <label className="block text-xs font-medium text-gray-600 mb-1.5">
            Tên dự án
          </label>
          <input
            ref={inputRef}
            type="text"
            value={name}
            onChange={(e) => setName(e.target.value)}
            maxLength={100}
            disabled={loading}
            className="w-full px-3.5 py-2.5 text-sm border border-gray-300 rounded-xl outline-none transition-colors focus:border-red-500 focus:ring-2 focus:ring-red-100 disabled:bg-gray-50"
          />
          <div className="mt-1.5 text-right text-[11px] text-gray-400">
            {trimmed.length}/100
          </div>

          <label className="block text-xs font-medium text-gray-600 mb-1.5 mt-2">
            Prompt dự án
          </label>
          <textarea
            value={prompt}
            onChange={(e) => setPrompt(e.target.value)}
            maxLength={10000}
            rows={6}
            placeholder="Hướng dẫn AI cách trả lời trong dự án này…"
            disabled={loading}
            className="w-full px-3.5 py-2.5 text-sm border border-gray-300 rounded-xl outline-none transition-colors focus:border-red-500 focus:ring-2 focus:ring-red-100 disabled:bg-gray-50 resize-y"
          />
          <div className="mt-1 text-right text-[11px] text-gray-400">
            {prompt.length}/10000
          </div>
        </div>

        <div className="flex items-center justify-end gap-2 px-5 py-3 border-t border-gray-100 bg-gray-50">
          <button
            type="button"
            disabled={loading}
            onClick={onClose}
            className="px-4 py-2 text-sm font-medium text-gray-700 hover:bg-gray-200/70 rounded-lg disabled:opacity-50 transition-colors"
          >
            Hủy
          </button>
          <button
            type="button"
            disabled={loading || !trimmed}
            onClick={submit}
            className="px-4 py-2 text-sm font-medium text-white rounded-lg bg-red-600 hover:bg-red-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
          >
            {loading ? "Đang lưu…" : "Lưu"}
          </button>
        </div>
      </div>
    </div>,
    document.body,
  );
}

export default EditProjectModal;
